import React from "react";
import { SvgIcon, SvgIconProps } from "@mui/material";
import settingsShellTokens from "screens/Settings/Settings.tokens";

const { nav } = settingsShellTokens;

/**
 * Line icons for the settings navigation, traced from the Settings design so
 * the stroke weight and geometry match it exactly (`@mui/icons-material` ships
 * filled glyphs at a different optical weight). Sizing and stroke width come
 * from the shell tokens — never inline them here.
 */
const NavIcon: React.FC<SvgIconProps> = ({ children, sx, ...rest }) => (
  <SvgIcon
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={nav.itemIconStrokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    sx={{
      width: nav.itemIconSize,
      height: nav.itemIconSize,
      flexShrink: 0,
      // SvgIcon's own styles set `fill: currentColor`, which would beat the
      // `fill="none"` attribute and flood these outline glyphs.
      fill: "none",
      ...sx,
    }}
    {...rest}
  >
    {children}
  </SvgIcon>
);

export const ProfileNavIcon: React.FC<SvgIconProps> = (props) => (
  <NavIcon {...props}>
    <circle cx="12" cy="8" r="4" />
    <path d="M4 21c0-4 3.6-6 8-6s8 2 8 6" />
  </NavIcon>
);

export const SecurityNavIcon: React.FC<SvgIconProps> = (props) => (
  <NavIcon {...props}>
    <path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z" />
  </NavIcon>
);

export const DelegationNavIcon: React.FC<SvgIconProps> = (props) => (
  <NavIcon {...props}>
    <path d="M17 3l4 4-4 4" />
    <path d="M21 7H7" />
    <path d="M7 21l-4-4 4-4" />
    <path d="M3 17h14" />
  </NavIcon>
);

export const TicketsNavIcon: React.FC<SvgIconProps> = (props) => (
  <NavIcon {...props}>
    <circle cx="12" cy="12" r="9" />
    <path d="M9.5 9a2.5 2.5 0 1 1 4 2c-.8.6-1.5 1.1-1.5 2" />
    <path d="M12 17h.01" />
  </NavIcon>
);

export const PlanNavIcon: React.FC<SvgIconProps> = (props) => (
  <NavIcon {...props}>
    <rect x="2" y="5" width="20" height="14" rx="2" />
    <path d="M2 10h20" />
  </NavIcon>
);

export const RolesNavIcon: React.FC<SvgIconProps> = (props) => (
  <NavIcon {...props}>
    <path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z" />
    <path d="M9 12l2 2 4-4" />
  </NavIcon>
);

export const TeamsNavIcon: React.FC<SvgIconProps> = (props) => (
  <NavIcon {...props}>
    <circle cx="9" cy="8" r="3.5" />
    <path d="M2.5 20c0-3.3 2.9-5 6.5-5s6.5 1.7 6.5 5" />
    <path d="M16 4.6a3.5 3.5 0 0 1 0 6.8" />
    <path d="M17.5 15.4c2.4.6 4 2 4 4.6" />
  </NavIcon>
);

export const DocumentsNavIcon: React.FC<SvgIconProps> = (props) => (
  <NavIcon {...props}>
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
    <path d="M14 3v5h5" />
  </NavIcon>
);

export const BrandingNavIcon: React.FC<SvgIconProps> = (props) => (
  <NavIcon {...props}>
    <circle cx="13.5" cy="6.5" r="2.5" />
    <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c1.1 0 2-.9 2-2 0-.5-.2-1-.5-1.3-.3-.4-.5-.8-.5-1.4 0-1.1.9-2 2-2h2.4c3.1 0 5.6-2.5 5.6-5.6C23 5.6 18 2 12 2z" />
    <circle cx="8" cy="8.5" r="1" />
    <circle cx="6.5" cy="13" r="1" />
  </NavIcon>
);

export const PoliciesNavIcon: React.FC<SvgIconProps> = (props) => (
  <NavIcon {...props}>
    <rect x="5" y="11" width="14" height="9" rx="2" />
    <path d="M8 11V8a4 4 0 0 1 8 0v3" />
  </NavIcon>
);

export const IntegrationsNavIcon: React.FC<SvgIconProps> = (props) => (
  <NavIcon {...props}>
    <path d="M10 4.5a1.8 1.8 0 0 1 3.6 0V6h3a1 1 0 0 1 1 1v3h1.5a1.8 1.8 0 0 1 0 3.6H17.6V17a1 1 0 0 1-1 1h-3.4v-1.5a1.8 1.8 0 0 0-3.6 0V18H6a1 1 0 0 1-1-1v-3.4h1.5a1.8 1.8 0 0 0 0-3.6H5V7a1 1 0 0 1 1-1h4z" />
  </NavIcon>
);

/**
 * Trailing "drill in" chevron on the phone index rows. It carries its own
 * stroke weight (the reference draws it heavier than the leading glyphs) and is
 * decorative — the row label already names the destination.
 */
export const NavChevronIcon: React.FC<SvgIconProps> = ({ sx, ...rest }) => (
  <NavIcon
    aria-hidden="true"
    strokeWidth={2.2}
    sx={{ flexShrink: 0, ...sx }}
    {...rest}
  >
    <path d="M9 6l6 6-6 6" />
  </NavIcon>
);

export const SsoNavIcon: React.FC<SvgIconProps> = (props) => (
  <NavIcon {...props}>
    <circle cx="8" cy="15" r="4" />
    <path d="M11 12l8-8" />
    <path d="M16 4l3 3" />
    <path d="M13.5 6.5l2 2" />
  </NavIcon>
);
